import React, { useRef } from "react";
import { Link } from "react-router-dom";
import "./CharacterCard.css";
import LazyImage from "./LazyImage"; // Import the LazyImage component
import "./LazyImage.css"; // Import the LazyImage styles

// This component now accepts animeStore as a prop
const CharacterCard = ({ character, animeStore = {} }) => {
  // All hooks must be called at the top level, before any conditional logic
  const cardRef = useRef(null);

  // Validation checks AFTER all hooks have been called
  if (!character || typeof character !== "object") {
    console.error(
      "Invalid character object provided to CharacterCard:",
      character
    );
    return (
      <div className="character-card error-card">Invalid character data</div>
    );
  }

  // More flexible ID handling - can be _id, id, or mal_id
  const characterId =
    character._id || character.id || character.mal_id || "unknown";

  // If we don't have an ID at all, still try to render but with a warning
  if (characterId === "unknown") {
    console.warn("Character card missing ID property:", character);
  }

  // Handle null/undefined values with better fallbacks
  const favorites = character.favorites ?? "N/A";
  const nameKanji = character.name_kanji ?? "";

  // Handle potential empty arrays with proper type checking
  const animeAppearances = Array.isArray(character.anime)
    ? character.anime
    : [];
  const mangaAppearances = Array.isArray(character.manga)
    ? character.manga
    : [];

  // Find most popular anime appearance
  const findMostPopularAnime = () => {
    if (animeAppearances.length === 0) return null;

    try {
      let bestEntry = animeAppearances[0]; // Default to first anime
      let bestPopularity = Number.MAX_SAFE_INTEGER;
      let bestMembers = 0;
      let bestFavorites = 0;

      // Loop through all anime appearances
      for (const animeEntry of animeAppearances) {
        if (!animeEntry.anime?.mal_id) continue;

        // Look up the full anime details from the store based on mal_id
        const fullAnimeData = animeStore[animeEntry.anime.mal_id];

        if (fullAnimeData) {
          // If we have the data, check if it's more popular
          if (
            fullAnimeData.popularity &&
            fullAnimeData.popularity < bestPopularity
          ) {
            bestEntry = animeEntry;
            bestPopularity = fullAnimeData.popularity;
          } else if (
            bestPopularity === Number.MAX_SAFE_INTEGER &&
            fullAnimeData.members &&
            fullAnimeData.members > bestMembers
          ) {
            bestEntry = animeEntry;
            bestMembers = fullAnimeData.members;
          } else if (
            bestPopularity === Number.MAX_SAFE_INTEGER &&
            bestMembers === 0 &&
            fullAnimeData.favorites &&
            fullAnimeData.favorites > bestFavorites
          ) {
            bestEntry = animeEntry;
            bestFavorites = fullAnimeData.favorites;
          }
        }
      }

      return bestEntry;
    } catch (error) {
      console.error("Error finding most popular anime:", error);
      return animeAppearances[0];
    }
  };

  // Find most popular manga appearance
  const findMostPopularManga = () => {
    // For now, just use the first manga in the list
    return mangaAppearances.length > 0 ? mangaAppearances[0] : null;
  };

  // Use most popular anime/manga if available, otherwise fall back to first one
  const animeToUse = findMostPopularAnime();
  const mangaToUse = findMostPopularManga();

  // Get primary anime title with fallbacks
  const primaryAnimeTitle = (() => {
    if (animeToUse && animeToUse.anime) {
      const anime = animeToUse.anime;
      return anime.title_english || anime.title || "N/A";
    }
    return "N/A";
  })();

  // Get primary manga title with fallbacks
  const primaryMangaTitle = (() => {
    if (mangaToUse && mangaToUse.manga) {
      const manga = mangaToUse.manga;
      return manga.title_english || manga.title || "N/A";
    }
    return "N/A";
  })();

  // Get role from the selected media
  const role = (() => {
    if (animeToUse && animeToUse.role) {
      return animeToUse.role;
    } else if (mangaToUse && mangaToUse.role) {
      return mangaToUse.role;
    }
    return "N/A";
  })();

  const age = character.age ?? "Unknown";
  const height = character.height ? `${character.height} cm` : "Unknown";

  // Get image with fallback
  const imageUrl =
    character.images?.webp?.imageUrl ||
    character.images?.webp?.image_url ||
    character.images?.jpg?.imageUrl ||
    character.images?.jpg?.image_url ||
    "/default-character.jpg";

  // Always use horizontal card class
  const cardClass = "character-card card-horizontal";

  // Determine which title to display
  const displayTitle =
    primaryAnimeTitle !== "N/A" ? primaryAnimeTitle : primaryMangaTitle;
  const titleTooltip =
    primaryAnimeTitle !== "N/A" ? primaryAnimeTitle : primaryMangaTitle;

  return (
    <div className={cardClass} ref={cardRef}>
      <Link to={`/characters/${characterId}`} className="card-link">
        <div className="card-image-container">
          {/* Replace img with LazyImage */}
          <LazyImage
            src={imageUrl}
            alt={character.name || "Character"}
            className="card-image"
            fallbackSrc="/default-character.jpg"
          />
          <div className="favorites-badge">
            ❤️{" "}
            {typeof favorites === "number"
              ? favorites.toLocaleString()
              : favorites}
          </div>
        </div>

        <div className="card-content">
          <div className="metadata-content">
            <h3 className="card-title">
              {character.name || "Unknown Character"}
            </h3>
            {nameKanji && <div className="name-kanji">{nameKanji}</div>}

            <div className="metadata-line">
              <span className="metadata-item">
                <span className="metadata-label">Role:</span>
                <span className="metadata-value">{role}</span>
              </span>
            </div>

            <div className="metadata-line mobile-hide">
              <span className="metadata-item">
                <span className="metadata-label">Appears in:</span>
                <span className="metadata-value" title={titleTooltip}>
                  {displayTitle}
                </span>
              </span>
            </div>
          </div>

          <div className="metadata-footer">
            <span className="metadata-item">
              <span className="metadata-label">Age:</span>
              <span className="metadata-value">{age}</span>
            </span>
            <span className="metadata-divider">•</span>
            <span className="metadata-item">
              <span className="metadata-label">Height:</span>
              <span className="metadata-value">{height}</span>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default CharacterCard;
